import { useState, useEffect } from 'react'
import { toast } from 'sonner'
import api from '@/services/api'
import { Modal } from '@/components/ui/Modal'
import { cn } from '@/utils/cn'
import type { EmailTemplate } from './EmailTemplatesTab'

interface EmailFieldDefinition {
  key: string
  label: string
  group: string
  description?: string
}

interface EmailTemplateModalProps {
  open: boolean
  onClose: () => void
  onSave: () => void
  template?: EmailTemplate | null
}

const bdrCategorias = [
  { value: 'lead.criado', label: 'Lead Criado' },
  { value: 'lead.nao_respondeu', label: 'Não Respondeu' },
  { value: 'nutricao', label: 'Nutrição' },
  { value: 'followup', label: 'Follow-up' },
  { value: 'reativacao', label: 'Reativação' },
]

const bdrVariaveis = [
  'lead.nome',
  'lead.empresa',
  'lead.contato',
  'lead.cargo',
  'lead.email',
  'lead.telefone',
  'lead.segmento',
  'lead.site',
]

type StructuredMeta = {
  layout?: 'structured' | 'html'
  title?: string
  intro?: string
  footer?: string
  ctaLabel?: string
}

function parseJson<T>(raw: string | null | undefined, fallback: T): T {
  if (!raw) return fallback
  try {
    return JSON.parse(raw) as T
  } catch {
    return fallback
  }
}

export function EmailTemplateModal({ open, onClose, onSave, template }: EmailTemplateModalProps) {
  const [nome, setNome] = useState('')
  const [assunto, setAssunto] = useState('')
  const [corpo, setCorpo] = useState('')
  const [categoria, setCategoria] = useState('')
  const [descricao, setDescricao] = useState('')
  const [variaveis, setVariaveis] = useState<string[]>([])
  const [campos, setCampos] = useState<string[]>([])
  const [meta, setMeta] = useState<StructuredMeta>({})
  const [fieldCatalog, setFieldCatalog] = useState<EmailFieldDefinition[]>([])
  const [saving, setSaving] = useState(false)

  const isEditing = !!template
  const isSystem = template?.isSystem ?? false
  const isStructured = (meta.layout ?? (isSystem ? 'structured' : 'html')) === 'structured'

  useEffect(() => {
    if (!open) return
    void api.get('/email/templates/field-catalog').then(({ data }) => {
      setFieldCatalog(data.data?.campos ?? [])
    })
  }, [open])

  useEffect(() => {
    if (template) {
      setNome(template.nome)
      setAssunto(template.assunto)
      setCorpo(template.corpo)
      setCategoria(template.categoria)
      setDescricao(template.descricao ?? '')
      setVariaveis(parseJson<string[]>(template.variaveis, []))
      setCampos(parseJson<string[]>(template.campos, []))
      setMeta(parseJson<StructuredMeta>(template.meta, { layout: template.isSystem ? 'structured' : 'html' }))
    } else {
      setNome('')
      setAssunto('')
      setCorpo('')
      setCategoria('')
      setDescricao('')
      setVariaveis([])
      setCampos([])
      setMeta({ layout: 'html' })
    }
  }, [template, open])

  const handleSave = async () => {
    if (!nome.trim() || !assunto.trim() || !categoria) {
      toast.error('Preencha os campos obrigatórios')
      return
    }
    if (!isStructured && !corpo.trim()) {
      toast.error('Preencha o corpo do e-mail')
      return
    }

    setSaving(true)
    try {
      const payload = {
        nome: nome.trim(),
        assunto: assunto.trim(),
        corpo: isStructured ? (meta.intro ?? corpo).trim() : corpo.trim(),
        categoria: isSystem && template ? template.categoria : categoria,
        descricao: descricao.trim() || undefined,
        variaveis: variaveis.length > 0 ? variaveis : undefined,
        campos: isStructured ? campos : undefined,
        meta: isStructured ? meta : { layout: 'html' },
      }

      if (isEditing && template) {
        await api.patch(`/email/templates/${template.id}`, payload)
        toast.success('Template atualizado')
      } else {
        await api.post('/email/templates', payload)
        toast.success('Template criado')
      }
      onSave()
    } catch {
      toast.error('Erro ao salvar template')
    } finally {
      setSaving(false)
    }
  }

  const insertVariable = (variable: string) => {
    const tag = variable.includes('.') ? `{{${variable}}}` : `{{${variable}}}`
    if (isStructured) {
      setMeta((prev) => ({ ...prev, intro: `${prev.intro ?? ''}${tag}` }))
    } else {
      setCorpo((prev) => prev + tag)
    }
  }

  const toggleCampo = (key: string) => {
    setCampos((prev) => (prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]))
  }

  const availableVariables = isSystem
    ? variaveis
    : bdrVariaveis

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEditing ? 'Editar Template' : 'Novo Template BDR'}
      description={
        isSystem
          ? 'Template de sistema — edite assunto, textos e quais informações aparecem no e-mail'
          : 'Configure o template de email para sequências BDR'
      }
      size="xl"
      footer={
        <div className="flex items-center gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-bold text-text-muted hover:text-text-primary hover:bg-white/[0.06] transition-all min-h-[44px]"
          >
            Cancelar
          </button>
          <button
            onClick={() => void handleSave()}
            disabled={saving}
            className={cn(
              'flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all min-h-[44px]',
              saving
                ? 'bg-accent/50 text-white/50 cursor-not-allowed'
                : 'bg-accent text-white hover:brightness-110',
            )}
          >
            {saving && (
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            )}
            {isEditing ? 'Salvar Alterações' : 'Criar Template'}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="flex flex-col gap-1.5">
          <label className="text-[11px] text-text-muted font-semibold uppercase tracking-wider">
            Nome do Template *
          </label>
          <input
            type="text"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            disabled={isSystem}
            className="w-full bg-white/[0.04] border border-white/[0.06] text-text-primary rounded-lg px-3 py-2.5 text-sm outline-none focus:border-accent transition-colors min-h-[44px] disabled:opacity-60"
          />
        </div>

        {isSystem && (
          <div className="rounded-lg border border-accent/20 bg-accent/5 px-3 py-2 text-xs text-text-muted space-y-1">
            <p>
              <span className="font-semibold text-text-primary">Gatilho:</span>{' '}
              {(meta as { gatilho?: string }).gatilho ?? descricao}
            </p>
            {template && template.systemKey ? (
              <p>
                <span className="font-semibold text-text-primary">Chave:</span> {template.systemKey}
              </p>
            ) : null}
          </div>
        )}

        <div className="flex flex-col gap-1.5">
          <label className="text-[11px] text-text-muted font-semibold uppercase tracking-wider">
            Assunto do Email *
          </label>
          <input
            type="text"
            value={assunto}
            onChange={(e) => setAssunto(e.target.value)}
            placeholder="Ex: Novo cliente — {{empresaNome}}"
            className="w-full bg-white/[0.04] border border-white/[0.06] text-text-primary rounded-lg px-3 py-2.5 text-sm outline-none focus:border-accent transition-colors min-h-[44px]"
          />
        </div>

        {!isSystem ? (
          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] text-text-muted font-semibold uppercase tracking-wider">
              Categoria *
            </label>
            <select
              value={categoria}
              onChange={(e) => setCategoria(e.target.value)}
              className="w-full bg-white/[0.04] border border-white/[0.06] text-text-primary rounded-lg px-3 py-2.5 text-sm outline-none focus:border-accent transition-colors min-h-[44px] appearance-none"
            >
              <option value="">Selecione...</option>
              {bdrCategorias.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        {isStructured ? (
          <>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] text-text-muted font-semibold uppercase tracking-wider">
                  Título do card
                </label>
                <input
                  type="text"
                  value={meta.title ?? ''}
                  onChange={(e) => setMeta((prev) => ({ ...prev, title: e.target.value }))}
                  className="w-full bg-white/[0.04] border border-white/[0.06] text-text-primary rounded-lg px-3 py-2.5 text-sm outline-none focus:border-accent"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] text-text-muted font-semibold uppercase tracking-wider">
                  Texto do botão
                </label>
                <input
                  type="text"
                  value={meta.ctaLabel ?? ''}
                  onChange={(e) => setMeta((prev) => ({ ...prev, ctaLabel: e.target.value }))}
                  className="w-full bg-white/[0.04] border border-white/[0.06] text-text-primary rounded-lg px-3 py-2.5 text-sm outline-none focus:border-accent"
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-[11px] text-text-muted font-semibold uppercase tracking-wider">
                Texto introdutório
              </label>
              <textarea
                value={meta.intro ?? ''}
                onChange={(e) => setMeta((prev) => ({ ...prev, intro: e.target.value }))}
                rows={3}
                className="w-full bg-white/[0.04] border border-white/[0.06] text-text-primary rounded-lg px-3 py-2.5 text-sm outline-none focus:border-accent resize-none"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-[11px] text-text-muted font-semibold uppercase tracking-wider">
                Rodapé
              </label>
              <textarea
                value={meta.footer ?? ''}
                onChange={(e) => setMeta((prev) => ({ ...prev, footer: e.target.value }))}
                rows={2}
                className="w-full bg-white/[0.04] border border-white/[0.06] text-text-primary rounded-lg px-3 py-2.5 text-sm outline-none focus:border-accent resize-none"
              />
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-[11px] text-text-muted font-semibold uppercase tracking-wider">
                Informações exibidas no e-mail
              </label>
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                {fieldCatalog.map((field) => (
                  <label
                    key={field.key}
                    className={cn(
                      'flex items-start gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer transition-colors',
                      campos.includes(field.key)
                        ? 'border-accent/40 bg-accent/10 text-text-primary'
                        : 'border-white/[0.06] bg-white/[0.03] text-text-muted',
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={campos.includes(field.key)}
                      onChange={() => toggleCampo(field.key)}
                      className="mt-1 accent-accent"
                    />
                    <span>
                      <span className="font-medium">{field.label}</span>
                      {field.description ? (
                        <span className="block text-[11px] text-text-muted">{field.description}</span>
                      ) : null}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] text-text-muted font-semibold uppercase tracking-wider">
              Corpo do Email (HTML) *
            </label>
            <textarea
              value={corpo}
              onChange={(e) => setCorpo(e.target.value)}
              rows={12}
              className="w-full bg-white/[0.04] border border-white/[0.06] text-text-primary rounded-lg px-3 py-2.5 text-sm outline-none focus:border-accent resize-none font-mono"
            />
          </div>
        )}

        <div className="flex flex-col gap-1.5">
          <label className="text-[11px] text-text-muted font-semibold uppercase tracking-wider">
            Variáveis disponíveis
          </label>
          <div className="flex flex-wrap gap-1.5">
            {availableVariables.map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => insertVariable(v)}
                className="px-2 py-1 rounded-md text-[11px] font-mono bg-white/[0.04] border border-white/[0.06] text-text-muted hover:text-accent hover:border-accent/30 transition-colors"
              >
                {`{{${v}}}`}
              </button>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  )
}
